import { Link } from 'react-router-dom'
import { LandingNav } from '@/components/landing'

export function PrivacyPage() {
  return (
    <div className="min-h-screen bg-primary-bg">
      <LandingNav />
      <div className="max-w-3xl mx-auto px-4 py-16">
        <h1 className="text-3xl font-medium mb-2">Privacy Policy</h1>
        <p className="text-sm text-secondary-text mb-12">Last updated: February 2, 2026</p>

        <div className="space-y-8 text-sm text-secondary-text leading-relaxed">
          <section>
            <h2 className="text-lg font-medium text-primary-text mb-3">1. Introduction</h2>
            <p>
              Beacon AI ("we", "us", "our") operates Beacon, an AI-powered content generation and
              scheduling platform. This Privacy Policy explains how we collect, use, and protect your
              information when you use our Service.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-medium text-primary-text mb-3">2. Information We Collect</h2>

            <h3 className="font-medium text-primary-text mt-4 mb-2">Account Information</h3>
            <p>
              When you create an account, we collect your email address and a hashed version of your
              password. We never store your password in plain text.
            </p>

            <h3 className="font-medium text-primary-text mt-4 mb-2">Social Media Data</h3>
            <p>
              When you connect your X (Twitter) account, we access your public posts to analyze your
              writing style. This analysis produces a voice profile containing aggregate style
              characteristics (tone, vocabulary, topics). We store the voice profile but do not
              permanently store copies of your individual posts beyond what is needed for analysis.
            </p>

            <h3 className="font-medium text-primary-text mt-4 mb-2">Generated Content</h3>
            <p>
              We store content you generate, edit, and manage through the Service, including drafts,
              approved posts, scheduled posts, and posting history.
            </p>

            <h3 className="font-medium text-primary-text mt-4 mb-2">Usage Data</h3>
            <p>
              We collect information about how you use the Service, including feature usage, generation
              counts, and error logs. We use Sentry for error tracking, which may collect browser
              information and error details to help us improve the Service.
            </p>

            <h3 className="font-medium text-primary-text mt-4 mb-2">Payment Information</h3>
            <p>
              Payments are processed by Stripe. We do not store your credit card number or payment
              details on our servers. Stripe's privacy policy governs their handling of your payment data.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-medium text-primary-text mb-3">3. How We Use Your Information</h2>
            <ul className="list-disc pl-5 space-y-1">
              <li>To provide the Service: generating content, scheduling posts, publishing to X</li>
              <li>To analyze your writing style and create voice profiles</li>
              <li>To process payments and manage subscriptions</li>
              <li>To send transactional emails (password resets, posting notifications)</li>
              <li>To monitor and improve the Service's reliability and performance</li>
              <li>To enforce our Terms of Service and prevent abuse</li>
            </ul>
          </section>

          <section>
            <h2 className="text-lg font-medium text-primary-text mb-3">4. Information Sharing</h2>
            <p className="mb-2">We do not sell your personal information. We share data only with:</p>
            <ul className="list-disc pl-5 space-y-1">
              <li><strong className="text-primary-text">X (Twitter):</strong> To publish content you approve and to fetch your public posts for voice analysis</li>
              <li><strong className="text-primary-text">Stripe:</strong> To process payments</li>
              <li><strong className="text-primary-text">AI providers:</strong> To generate content (prompts and voice profiles are sent to our AI provider; your email and account details are not)</li>
              <li><strong className="text-primary-text">Sentry:</strong> For error tracking and service reliability</li>
              <li><strong className="text-primary-text">Law enforcement:</strong> If required by law or to protect the rights and safety of our users</li>
            </ul>
          </section>

          <section>
            <h2 className="text-lg font-medium text-primary-text mb-3">5. Data Security</h2>
            <p>
              We use industry-standard measures to protect your data, including encrypted connections
              (HTTPS), hashed passwords, and secure token-based authentication. However, no method of
              transmission over the internet is 100% secure.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-medium text-primary-text mb-3">6. Data Retention</h2>
            <p>
              We retain your account data and generated content for as long as your account is active.
              If you delete your account, we will delete your personal data within 30 days, except
              where retention is required by law or for legitimate business purposes (e.g., billing records).
            </p>
          </section>

          <section>
            <h2 className="text-lg font-medium text-primary-text mb-3">7. Your Rights</h2>
            <p className="mb-2">Depending on your jurisdiction, you may have the right to:</p>
            <ul className="list-disc pl-5 space-y-1">
              <li>Access the personal data we hold about you</li>
              <li>Request correction of inaccurate data</li>
              <li>Request deletion of your data</li>
              <li>Disconnect your social media accounts at any time</li>
              <li>Export your generated content</li>
            </ul>
            <p className="mt-2">
              To exercise these rights, contact us at{' '}
              <a href="mailto:support@globalringai.com" className="text-primary-text hover:underline">
                support@globalringai.com
              </a>.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-medium text-primary-text mb-3">8. Cookies</h2>
            <p>
              The Service uses local storage to maintain your authentication session. We do not use
              third-party tracking cookies. Sentry may use cookies for error tracking purposes.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-medium text-primary-text mb-3">9. Children's Privacy</h2>
            <p>
              The Service is not intended for users under 18. We do not knowingly collect information
              from children. If we learn that we have collected data from a child, we will delete it promptly.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-medium text-primary-text mb-3">10. Changes to This Policy</h2>
            <p>
              We may update this policy from time to time. We will notify you of material changes via
              email or through the Service. The "Last updated" date at the top reflects when the policy
              was last revised.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-medium text-primary-text mb-3">11. Contact</h2>
            <p>
              If you have questions about this policy, contact us at{' '}
              <a href="mailto:support@globalringai.com" className="text-primary-text hover:underline">
                support@globalringai.com
              </a>.
            </p>
          </section>
        </div>

        <div className="mt-12 pt-8 border-t border-border flex items-center justify-between">
          <Link to="/" className="text-sm text-secondary-text hover:text-primary-text transition-colors">
            &larr; Back to home
          </Link>
          <Link to="/terms" className="text-sm text-secondary-text hover:text-primary-text transition-colors">
            Terms of Service &rarr;
          </Link>
        </div>
      </div>
    </div>
  )
}
